import React, { useState, useEffect } from 'react';
import { gstReportAPI, formatCurrency } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';

const PIE_COLORS = ['#6366f1', '#8b5cf6', '#a855f7'];

export default function GSTReports() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    gstReportAPI.get().then(res => setData(res.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);

  if (user?.role !== 'admin') return <div className="text-center py-20"><p className="text-slate-500">Admin access required</p></div>;
  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;

  const totals = data?.totals || {};
  const monthly = data?.monthly || [];
  const pieData = [
    { name: 'CGST', value: totals.cgst || 0 },
    { name: 'SGST', value: totals.sgst || 0 },
    { name: 'IGST', value: totals.igst || 0 },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="gst-reports-page" className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900">GST Reports</h1>
        <p className="text-sm text-slate-500 font-body mt-1">Tax collection summary & filing data</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[['Total Taxable', totals.taxable, '#334155'],['CGST', totals.cgst, '#6366f1'],['SGST', totals.sgst, '#8b5cf6'],['IGST', totals.igst, '#a855f7']].map(([label, val, color], i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`gst-card-${label.toLowerCase()}`}>
            <p className="text-xs font-semibold text-slate-500 uppercase">{label}</p>
            <p className="text-xl font-bold font-mono mt-1" style={{ color }}>Rs. {formatCurrency(val || 0)}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Monthly GST Bar Chart */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="monthly-gst-chart">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Monthly Tax Collection</h3>
          {monthly.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthly} barCategoryGap="15%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => [`Rs. ${formatCurrency(v)}`]} />
                <Bar dataKey="cgst" stackId="a" fill="#6366f1" name="CGST" />
                <Bar dataKey="sgst" stackId="a" fill="#8b5cf6" name="SGST" />
                <Bar dataKey="igst" stackId="a" fill="#a855f7" name="IGST" radius={[4,4,0,0]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">No data</div>}
        </div>

        {/* GST Breakdown Pie */}
        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="gst-pie-chart">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">GST Type Distribution</h3>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value"
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} stroke="none" />)}
                </Pie>
                <Tooltip formatter={v => [`Rs. ${formatCurrency(v)}`]} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">No data</div>}
        </div>
      </div>

      {/* Monthly Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200"><h3 className="font-heading font-semibold text-slate-800">Monthly Breakdown</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full data-table" data-testid="gst-monthly-table">
            <thead><tr><th>Month</th><th className="text-right">Invoices</th><th className="text-right">Taxable Amt</th><th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th><th className="text-right">Total Tax</th><th className="text-right">Grand Total</th></tr></thead>
            <tbody>
              {monthly.map((m, i) => (
                <tr key={i}><td className="font-mono text-sm">{m.month}</td><td className="text-right font-mono">{m.count}</td>
                  <td className="text-right font-mono">Rs.{formatCurrency(m.taxable)}</td><td className="text-right font-mono">Rs.{formatCurrency(m.cgst)}</td>
                  <td className="text-right font-mono">Rs.{formatCurrency(m.sgst)}</td><td className="text-right font-mono">Rs.{formatCurrency(m.igst)}</td>
                  <td className="text-right font-mono font-medium">Rs.{formatCurrency(m.total_tax)}</td><td className="text-right font-mono font-bold">Rs.{formatCurrency(m.grand_total)}</td></tr>
              ))}
              {monthly.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
